import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  makeStyles,
  CircularProgress,
  Link,
  Box,
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  error: {
    backgroundColor: theme.palette.error.dark,
  },
  success: {
    backgroundColor: theme.palette.success.dark,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  checkEmailMessage: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  codeInput: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  codeField: {
    '& input': {
      textAlign: 'center',
      fontSize: '1.5rem',
      letterSpacing: '0.5rem',
      fontWeight: 'bold',
    },
  },
}));

const LoginForm = ({ onLogin, onVerifyCode }) => {
  const classes = useStyles();
  const [formLoading, setFormLoading] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
  });
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [codeSent, setCodeSent] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);

    // Auto-submit when 6 digits are entered
    if (value.length === 6) {
      handleVerifyCode(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError(null);

    try {
      const result = await onLogin(formData.email);
      if (result.success) {
        setCodeSent(true);
      } else {
        // Handle different possible error structures
        const errorMessage =
          result?.message ||
          result?.error?.message ||
          'Failed to send verification code';
        setError(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to send verification code';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleVerifyCode = async (codeToVerify = code) => {
    setVerifyingCode(true);
    setError(null);

    try {
      // This will need to be implemented in the parent component
      const result = await onVerifyCode(formData.email, codeToVerify);
      if (result.success) {
        // Handle successful login (redirect, etc.)
      } else {
        const errorMessage =
          result?.message ||
          result?.error?.message ||
          'Invalid or expired code';
        setError(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to verify code';
      setError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResendCode = async () => {
    setFormLoading(true);
    setError(null);

    setCode('');

    try {
      const result = await onLogin(formData.email);
      if (result.success) {
      } else {
        const errorMessage =
          result?.message ||
          result?.error?.message ||
          'Failed to resend verification code';
        setError(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error.message || 'Failed to resend verification code';
      setError(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  if (codeSent) {
    return (
      <>
        <Typography variant="h5" gutterBottom>
          Check Your Email
        </Typography>
        <div className={classes.checkEmailMessage}>
          <Typography variant="body1" gutterBottom>
            We've sent a verification code to <strong>{formData.email}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Enter the 6-digit code below to sign in to your account.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            If you don't see the email, check your spam folder.
          </Typography>
          {error && (
            <Typography variant="body2" color="error" gutterBottom>
              {error}
            </Typography>
          )}
          <Box className={classes.codeInput}>
            <TextField
              variant="outlined"
              fullWidth
              id="code"
              label="Enter 6-digit code"
              name="code"
              type="text"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={handleCodeChange}
              disabled={verifyingCode}
              className={classes.codeField}
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]*',
                inputMode: 'numeric',
              }}
            />
          </Box>

          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleVerifyCode()}
              disabled={verifyingCode || code.length !== 6}
              fullWidth
              style={{ marginBottom: 16 }}
            >
              {verifyingCode ? <CircularProgress size={20} /> : 'Verify Code'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleResendCode}
              disabled={formLoading}
              style={{ marginRight: 16 }}
            >
              {formLoading ? <CircularProgress size={20} /> : 'Resend Code'}
            </Button>
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                setCodeSent(false);
                setFormData({ email: '' });
                setCode('');
              }}
            >
              Use Different Email
            </Button>
          </Box>
        </div>
      </>
    );
  }

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Sign In
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Enter your email address and we'll send you a verification code to sign
        in.
      </Typography>
      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error}
        </Typography>
      )}
      <form className={classes.form} onSubmit={handleSubmit}>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={formData.email}
          onChange={handleInputChange}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          disabled={formLoading || !formData.email.trim()}
        >
          {formLoading ? (
            <CircularProgress size={24} />
          ) : (
            'Send Verification Code'
          )}
        </Button>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Signed in users can set their address and contact information which
          will be used automatically when ordering.
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Accounts are controlled by your email address, if you have more than
          one email address make sure you use the correct one when signing in.
        </Typography>
      </form>
    </>
  );
};

export default LoginForm;
